import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Boxes, Glasses, MessageSquare, Pencil, Users, Wallet } from 'lucide-react';
import {
  REPORT_PRESETS,
  ReportPreset,
  SMART_FILTER_FIELDS,
  SMART_TOP_N,
  SmartComparison,
  SmartSort,
  type SmartBuilderCatalogue,
  type SmartFilterCode,
  type SmartMetricOption,
  type SmartReportArea,
  type SmartReportFilters,
} from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/utils/cn';
import { useSmartOptionsQuery } from '../api/smart.api';
import {
  choosableFilters,
  customRangeProblem,
  stepsFor,
  visualizationsFor,
  withDimension,
  withMetric,
  type SmartDraft,
  type SmartStep,
} from './smartDraft';
import {
  areaLabel,
  codeLabel,
  dimensionLabel,
  filterLabel,
  metricHint,
  metricKey,
  metricLabel,
} from './smartLabels';
import { useSummaryChips, type SmartSummaryChip } from './smartSummary';

const AREA_ICONS: Record<SmartReportArea, typeof BarChart3> = {
  SALES: BarChart3,
  INVENTORY: Boxes,
  FINANCE: Wallet,
  CUSTOMERS: Users,
  OPTICAL: Glasses,
  CRM: MessageSquare,
};

/** A choice button: pressed state is announced, and the whole card is the target. */
function Choice({
  selected,
  onClick,
  children,
  className,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
  label?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'rounded-lg border px-3 py-2 text-start text-sm transition-colors focus-visible:ring-4 focus-visible:ring-brand-100 focus-visible:outline-none',
        selected
          ? 'border-brand-600 bg-brand-50 text-brand-800 font-semibold'
          : 'border-line text-ink hover:border-brand-300 bg-white',
        className,
      )}
    >
      {children}
    </button>
  );
}

/** One of OptiFlow's questions, in the builder's conversational voice. */
function Question({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span
        aria-hidden
        className="bg-brand-600 mt-0.5 grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold text-white"
      >
        O
      </span>
      <h3
        id={id}
        className="text-ink bg-canvas rounded-xl rounded-ss-sm px-3 py-2 text-sm font-semibold"
      >
        {children}
      </h3>
    </div>
  );
}

export function SummaryChips({
  chips,
  onEdit,
  labelledBy,
}: {
  chips: SmartSummaryChip[];
  onEdit: (step: SmartStep) => void;
  labelledBy: string;
}) {
  const { t } = useTranslation();
  return (
    <ul aria-labelledby={labelledBy} className="flex flex-wrap gap-2">
      {chips.map((chip, index) => (
        <li key={`${chip.step}-${index}`}>
          <button
            type="button"
            onClick={() => onEdit(chip.step)}
            aria-label={t('reports.smart.editChip', { choice: chip.label })}
            className="border-brand-200 bg-brand-50 text-brand-800 hover:border-brand-400 inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1 text-sm break-words"
          >
            <span className="min-w-0 break-words">{chip.label}</span>
            <Pencil className="size-3 shrink-0" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * The conversational builder: one question at a time, each answered by choosing, with every answer
 * so far kept above as a line of the conversation that can be reopened. The questions asked come
 * from the metric the server offered — a figure as of now is never asked for a period, a figure
 * with no breakdowns is never asked how to break it down.
 */
export function SmartBuilder({
  catalogue,
  draft,
  step,
  onDraft,
  onStep,
  onGenerate,
  generating,
}: {
  catalogue: SmartBuilderCatalogue;
  draft: SmartDraft;
  step: SmartStep;
  onDraft: (draft: SmartDraft) => void;
  onStep: (step: SmartStep) => void;
  onGenerate: () => void;
  generating: boolean;
}) {
  const { t } = useTranslation();
  const steps = stepsFor(draft);
  const index = Math.max(0, steps.indexOf(step));
  const metric = draft.metric;
  const chips = useSummaryChips(draft, catalogue);
  const rangeProblem =
    metric && !metric.asOfNow ? customRangeProblem(draft.period, catalogue.maxRangeDays) : null;

  const canContinue = (() => {
    switch (step) {
      case 'area':
        return draft.area !== null;
      case 'metric':
        return draft.metric !== null;
      case 'period':
        return rangeProblem === null;
      default:
        return true;
    }
  })();
  const next = () => {
    const following = stepsFor(draft)[index + 1];
    if (following) onStep(following);
  };
  const back = () => {
    const previous = steps[index - 1];
    if (previous) onStep(previous);
  };

  const answered = steps.slice(0, index);
  const answerFor = (entry: SmartStep): string => {
    switch (entry) {
      case 'area':
        return draft.area ? areaLabel(t, draft.area) : '';
      case 'metric':
        return metric ? metricLabel(t, metric) : '';
      case 'period':
        return chips
          .filter((chip) => chip.step === 'period')
          .map((chip) => chip.label)
          .join(' · ');
      case 'breakdown':
        return (
          chips
            .filter((chip) => chip.step === 'breakdown')
            .map((chip) => chip.label)
            .join(' · ') || t('reports.smart.noBreakdown')
        );
      case 'filters':
        return chips
          .filter((chip) => chip.step === 'filters')
          .map((chip) => chip.label)
          .join(' · ');
      case 'visual':
        return chips
          .filter((chip) => chip.step === 'visual')
          .map((chip) => chip.label)
          .join(' · ');
      default:
        return '';
    }
  };

  return (
    <Card className="min-w-0 p-4 sm:p-5">
      <ol className="space-y-3" aria-label={t('reports.smart.conversation')}>
        {answered.map((entry) => (
          <li key={entry} className="space-y-1.5">
            <p className="text-muted text-xs">{t(`reports.smart.questions.${entry}`)}</p>
            <button
              type="button"
              onClick={() => onStep(entry)}
              aria-label={t('reports.smart.editChip', { choice: answerFor(entry) })}
              className="bg-brand-600 ms-auto flex max-w-full items-center gap-1.5 rounded-xl rounded-ee-sm px-3 py-1.5 text-sm text-white"
            >
              <span className="min-w-0 break-words">{answerFor(entry)}</span>
              <Pencil className="size-3 shrink-0" aria-hidden />
            </button>
          </li>
        ))}
        <li className="space-y-3" aria-current="step">
          <Question id={`smart-step-${step}`}>{t(`reports.smart.questions.${step}`)}</Question>
          <div role="group" aria-labelledby={`smart-step-${step}`}>
            {step === 'area' ? (
              <AreaStep
                catalogue={catalogue}
                draft={draft}
                onDraft={onDraft}
                onChosen={() => onStep('metric')}
              />
            ) : null}
            {step === 'metric' ? (
              <MetricStep catalogue={catalogue} draft={draft} onDraft={onDraft} />
            ) : null}
            {step === 'period' && metric ? (
              <PeriodStep
                metric={metric}
                draft={draft}
                onDraft={onDraft}
                problem={rangeProblem}
                maxDays={catalogue.maxRangeDays}
              />
            ) : null}
            {step === 'breakdown' && metric ? (
              <BreakdownStep metric={metric} draft={draft} onDraft={onDraft} />
            ) : null}
            {step === 'filters' && metric ? (
              <FiltersStep catalogue={catalogue} metric={metric} draft={draft} onDraft={onDraft} />
            ) : null}
            {step === 'visual' && metric ? (
              <VisualStep metric={metric} draft={draft} onDraft={onDraft} />
            ) : null}
            {step === 'review' ? (
              <div className="space-y-3">
                <p id="smart-understands" className="text-muted text-sm">
                  {t('reports.smart.understandsAs')}
                </p>
                <SummaryChips chips={chips} onEdit={onStep} labelledBy="smart-understands" />
                {metric?.asOfNow ? (
                  <p className="text-muted text-xs">{t('reports.smart.asOfNowNote')}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </li>
      </ol>

      <div className="border-line sticky bottom-0 -mx-4 mt-4 flex items-center justify-between gap-3 border-t bg-white px-4 pt-3 pb-1 sm:static sm:mx-0 sm:px-0">
        <Button variant="secondary" onClick={back} disabled={index === 0}>
          {t('reports.smart.back')}
        </Button>
        {step === 'review' ? (
          <Button
            onClick={onGenerate}
            isLoading={generating}
            disabled={!metric || rangeProblem !== null}
          >
            {t('reports.smart.generate')}
          </Button>
        ) : (
          <Button onClick={next} disabled={!canContinue}>
            {t('reports.smart.continue')}
          </Button>
        )}
      </div>
    </Card>
  );
}

function AreaStep({
  catalogue,
  draft,
  onDraft,
  onChosen,
}: {
  catalogue: SmartBuilderCatalogue;
  draft: SmartDraft;
  onDraft: (draft: SmartDraft) => void;
  onChosen: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {catalogue.areas.map(({ area }) => {
        const Icon = AREA_ICONS[area];
        return (
          <Choice
            key={area}
            selected={draft.area === area}
            onClick={() => {
              onDraft({ ...draft, area, metric: draft.area === area ? draft.metric : null });
              onChosen();
            }}
            className="flex items-start gap-3 p-3"
          >
            <Icon className="text-brand-600 mt-0.5 size-5 shrink-0" aria-hidden />
            <span className="min-w-0">
              <span className="block font-semibold">{areaLabel(t, area)}</span>
              <span className="text-muted block text-xs font-normal">
                {t(`reports.smart.areaHints.${area}`)}
              </span>
            </span>
          </Choice>
        );
      })}
    </div>
  );
}

function MetricStep({
  catalogue,
  draft,
  onDraft,
}: {
  catalogue: SmartBuilderCatalogue;
  draft: SmartDraft;
  onDraft: (draft: SmartDraft) => void;
}) {
  const { t } = useTranslation();
  const metrics = catalogue.areas.find((entry) => entry.area === draft.area)?.metrics ?? [];
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {metrics.map((metric) => (
        <Choice
          key={metricKey(metric)}
          selected={draft.metric !== null && metricKey(draft.metric) === metricKey(metric)}
          onClick={() => onDraft(withMetric(draft, metric))}
          className="p-3"
        >
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{metricLabel(t, metric)}</span>
            {metric.asOfNow ? (
              <span className="bg-canvas text-muted rounded px-1.5 py-0.5 text-[11px] font-medium">
                {t('reports.asOfNow')}
              </span>
            ) : null}
          </span>
          <span className="text-muted mt-0.5 block text-xs font-normal">
            {metricHint(t, metric)}
          </span>
        </Choice>
      ))}
    </div>
  );
}

function PeriodStep({
  metric,
  draft,
  onDraft,
  problem,
  maxDays,
}: {
  metric: SmartMetricOption;
  draft: SmartDraft;
  onDraft: (draft: SmartDraft) => void;
  problem: ReturnType<typeof customRangeProblem>;
  maxDays: number;
}) {
  const { t } = useTranslation();
  const field = 'border-line w-full rounded-lg border bg-white px-3 py-2 text-sm';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {REPORT_PRESETS.map((preset) => (
          <Choice
            key={preset}
            selected={draft.period.preset === preset}
            onClick={() =>
              onDraft({
                ...draft,
                period:
                  preset === ReportPreset.CUSTOM
                    ? { preset, from: draft.period.from, to: draft.period.to }
                    : { preset },
              })
            }
          >
            {t(`reports.presets.${preset}`)}
          </Choice>
        ))}
      </div>
      {draft.period.preset === ReportPreset.CUSTOM ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-muted text-xs font-medium">
            {t('reports.filters.from')}
            <input
              id="smart-from"
              type="date"
              className={cn(field, 'mt-1')}
              value={draft.period.from ?? ''}
              onChange={(event) =>
                onDraft({ ...draft, period: { ...draft.period, from: event.target.value } })
              }
            />
          </label>
          <label className="text-muted text-xs font-medium">
            {t('reports.filters.to')}
            <input
              id="smart-to"
              type="date"
              className={cn(field, 'mt-1')}
              value={draft.period.to ?? ''}
              onChange={(event) =>
                onDraft({ ...draft, period: { ...draft.period, to: event.target.value } })
              }
            />
          </label>
          {problem ? (
            <p role="alert" className="text-sm text-amber-700 sm:col-span-2">
              {t(`reports.smart.rangeProblems.${problem}`, { days: maxDays })}
            </p>
          ) : null}
        </div>
      ) : null}
      {metric.comparable ? (
        <div className="space-y-2">
          <p className="text-muted text-xs">{t('reports.smart.questions.comparison')}</p>
          <div className="flex flex-wrap gap-2">
            {Object.values(SmartComparison).map((comparison) => (
              <Choice
                key={comparison}
                selected={draft.comparison === comparison}
                onClick={() => onDraft({ ...draft, comparison })}
              >
                {t(`reports.smart.comparison.${comparison}`)}
              </Choice>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BreakdownStep({
  metric,
  draft,
  onDraft,
}: {
  metric: SmartMetricOption;
  draft: SmartDraft;
  onDraft: (draft: SmartDraft) => void;
}) {
  const { t } = useTranslation();
  const rankable = draft.dimension !== null && metric.rankable.includes(draft.dimension);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Choice
          selected={draft.dimension === null}
          onClick={() => onDraft(withDimension(draft, null))}
        >
          {t('reports.smart.noBreakdown')}
        </Choice>
        {metric.dimensions.map((dimension) => (
          <Choice
            key={dimension}
            selected={draft.dimension === dimension}
            onClick={() => onDraft(withDimension(draft, dimension))}
          >
            {dimensionLabel(t, dimension, metric)}
          </Choice>
        ))}
      </div>
      {rankable ? (
        <div className="space-y-2">
          <p className="text-muted text-xs">{t('reports.smart.questions.ranking')}</p>
          <div className="flex flex-wrap gap-2">
            <Choice
              selected={draft.limit === null}
              onClick={() => onDraft({ ...draft, limit: null })}
            >
              {t('reports.smart.allRows')}
            </Choice>
            {SMART_TOP_N.map((limit) => (
              <Choice
                key={limit}
                selected={draft.limit === limit}
                onClick={() => onDraft({ ...draft, limit })}
              >
                {t('reports.smart.topN', { count: limit })}
              </Choice>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.values(SmartSort).map((sort) => (
              <Choice
                key={sort}
                selected={draft.sort === sort}
                onClick={() => onDraft({ ...draft, sort })}
              >
                {t(`reports.smart.sort.${sort}`)}
              </Choice>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FiltersStep({
  catalogue,
  metric,
  draft,
  onDraft,
}: {
  catalogue: SmartBuilderCatalogue;
  metric: SmartMetricOption;
  draft: SmartDraft;
  onDraft: (draft: SmartDraft) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {choosableFilters(metric).map((filter) => (
        <FilterField
          key={filter}
          catalogue={catalogue}
          metric={metric}
          filter={filter}
          draft={draft}
          onDraft={onDraft}
        />
      ))}
    </div>
  );
}

function FilterField({
  catalogue,
  metric,
  filter,
  draft,
  onDraft,
}: {
  catalogue: SmartBuilderCatalogue;
  metric: SmartMetricOption;
  filter: SmartFilterCode;
  draft: SmartDraft;
  onDraft: (draft: SmartDraft) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const key = SMART_FILTER_FIELDS[filter] as keyof SmartReportFilters;
  const value = (draft.filters[key] as string | undefined) ?? '';
  const branchOnly = filter === 'BRANCH';
  const options = useSmartOptionsQuery(
    metric.code,
    metric.variant,
    filter,
    metric.asOfNow ? null : draft.period,
    {
      enabled: !branchOnly,
    },
  );
  const choices = branchOnly
    ? catalogue.branches.map((branch) => ({ value: branch.id, label: branch.name }))
    : (options.data?.options ?? []).map((option) => ({
        value: option.value,
        label:
          (locale === 'ar' ? (option.labelAr ?? option.label) : (option.label ?? option.labelAr)) ??
          codeLabel(t, filter, option.value),
      }));
  const id = `smart-filter-${filter}`;
  return (
    <div>
      <label htmlFor={id} className="text-muted mb-1 block text-xs font-medium">
        {filterLabel(t, filter, metric)}
      </label>
      <select
        id={id}
        className="border-line w-full rounded-lg border bg-white px-3 py-2 text-sm"
        value={value}
        onChange={(event) => {
          const chosen = event.target.value || undefined;
          const name = choices.find((choice) => choice.value === chosen)?.label;
          onDraft({
            ...draft,
            filters: { ...draft.filters, [key]: chosen },
            filterNames: { ...draft.filterNames, [key]: name },
          });
        }}
      >
        <option value="">
          {branchOnly ? t('reports.smart.allAccessibleBranches') : t('reports.smart.anyValue')}
        </option>
        {choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
      {!branchOnly && options.isError ? (
        <p className="text-muted mt-1 text-xs">{t('reports.smart.optionsUnavailable')}</p>
      ) : null}
    </div>
  );
}

function VisualStep({
  metric,
  draft,
  onDraft,
}: {
  metric: SmartMetricOption;
  draft: SmartDraft;
  onDraft: (draft: SmartDraft) => void;
}) {
  const { t } = useTranslation();
  const shapes = visualizationsFor(metric, draft.dimension);
  const current =
    draft.visualization && shapes.includes(draft.visualization) ? draft.visualization : shapes[0];
  return (
    <div className="flex flex-wrap gap-2">
      {shapes.map((shape, index) => (
        <Choice
          key={shape}
          selected={current === shape}
          onClick={() => onDraft({ ...draft, visualization: shape })}
        >
          {t(`reports.smart.visualizations.${shape}`)}
          {index === 0 ? (
            <span className="text-muted ms-1 text-xs font-normal">
              ({t('reports.smart.recommended')})
            </span>
          ) : null}
        </Choice>
      ))}
    </div>
  );
}
